import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { apiEndpoint } from "../config/api";
import { apiFetch } from "../services/api";

// The canonical level catalog (app.lksi_level_catalog) -- the set of levels a class can be mapped
// to. Lives in the Admin surface beside BdmDirectory because it is reference-data stewardship, not
// part of the Class Audit workflow.
//
// ADD ONLY. There is deliberately no rename, edit, deactivate or delete, and no disabled
// placeholders for them: mutating an EXISTING level has a far larger blast radius than adding one
// -- a rename drifts the level attributes denormalised onto ~8.4M registration fact rows, remedied
// only by a ~70-minute full refresh. Adding is purely additive and maps nothing.
//
// PLACEMENT IS EXPLICIT, NOT INFERRED. The steward picks "place after <level>"; the new level takes
// that level's sort order and is separated from it by its (immutable, generated) level_id. No
// existing row is ever renumbered. Raw sort integers are shown in the table for transparency but
// are never asked for.
//
// TWO CLOCKS. Active rules / Active overrides are app state and exact. Classes mapped is
// warehouse-derived from the last dbt build, so a level added today reads 0 until the next build
// even if a rule already targets it; the column is dated rather than presented as live.
const fmtDate = (x) => (x ? String(x).slice(0, 10) : "—");
const PLACE_FIRST = "__first__";

export default function CanonicalLevels() {
  const [levels, setLevels] = useState([]);
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [name, setName] = useState("");
  const [programType, setProgramType] = useState("LK");
  const [placeAfter, setPlaceAfter] = useState(PLACE_FIRST);
  const [ageGroup, setAgeGroup] = useState("");
  const [levelNumber, setLevelNumber] = useState("");
  const [ruleTargetable, setRuleTargetable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addNotice, setAddNotice] = useState(null);

  const fetchLevels = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiFetch(`${apiEndpoint}/canonical-levels`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load canonical levels");
      setLevels(data.levels || []);
      setWarehouse(data.warehouse || null);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLevels(); }, []);

  // Anchors are restricted to the SAME program: ordering is only ever consumed program-filtered, so
  // anchoring across programs would place the level somewhere no user ever looks.
  const anchors = useMemo(
    () => levels.filter((l) => l.program_type === programType && l.is_active),
    [levels, programType],
  );
  useEffect(() => { setPlaceAfter(PLACE_FIRST); }, [programType]);

  const canSave = name.trim() !== "" && !saving;

  const handleAdd = async () => {
    setSaving(true);
    setAddError(null);
    setAddNotice(null);
    try {
      const res = await apiFetch(`${apiEndpoint}/canonical-level-create`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8" },
        body: JSON.stringify({
          canonical_level_name: name.trim(),
          program_type: programType,
          place_after_level_id: placeAfter === PLACE_FIRST ? null : placeAfter,
          age_group: ageGroup.trim() || null,
          level_number: levelNumber.trim() || null,
          is_rule_targetable: ruleTargetable,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to add the level");
      setAddNotice(`Added ${data.level.canonical_level_name} (level_id ${data.level.level_id}).`
        + " It is selectable immediately; the warehouse copy catches up on the next build.");
      setName(""); setAgeGroup(""); setLevelNumber(""); setRuleTargetable(true);
      await fetchLevels();
    } catch (e) {
      setAddError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const builtAt = warehouse && warehouse.suggestions_built_at
    ? fmtDate(warehouse.suggestions_built_at) : null;

  // Rendered in the order the server sent: level_sort_order then level_id. Since 2026-08-25 the
  // catalog is renumbered to a contiguous 1..N on every insert, so level_sort_order alone is
  // sufficient and ties should be impossible. level_id remains only as a determinism guard -- it is
  // NOT here because a new level shares its anchor's sort order; that was the bug it used to mask.
  const rowEls = levels.map((l) => (
    <TableRow key={l.level_id} hover>
      <TableCell className="text-start">{l.canonical_level_name}</TableCell>
      <TableCell className="text-start">
        <Chip size="small" variant="outlined" label={l.program_type} />
      </TableCell>
      <TableCell className="text-start text-secondary">{l.age_group || "—"}</TableCell>
      <TableCell className="text-start text-secondary">{l.level_number ?? "—"}</TableCell>
      <TableCell className="text-start text-secondary">{l.level_sort_order}</TableCell>
      <TableCell className="text-start">
        <Chip size="small" variant="outlined" color={l.is_active ? "success" : "default"}
          label={l.is_active ? "Active" : "Inactive"} />
      </TableCell>
      <TableCell className="text-start">
        {l.is_rule_targetable === 0
          ? <Chip size="small" variant="outlined" color="warning" label="Manual only"
              title="Not offered as a reusable-rule target; assign it on the class." />
          : <Typography variant="caption" color="text.secondary">Rules + manual</Typography>}
      </TableCell>
      <TableCell className="text-start">{l.classes_mapped}</TableCell>
      <TableCell className="text-start">{l.active_rules}</TableCell>
      <TableCell className="text-start">{l.active_overrides}</TableCell>
      <TableCell className="text-start text-secondary" style={{ overflowWrap: "anywhere" }}>
        {l.created_by} · {fmtDate(l.created_at)}
      </TableCell>
    </TableRow>
  ));

  const anchorItems = anchors.map((l) => (
    <MenuItem key={l.level_id} value={l.level_id}>{l.canonical_level_name}</MenuItem>
  ));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" component="h2" fontWeight={600}>Canonical Levels</Typography>
        <Typography variant="body2" color="text.secondary">
          The set of levels a class can be mapped to. Levels are applied to classes in Class Audit
          and by reusable rules in Rule Management. Adding a level here maps nothing on its own.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Classes mapped is warehouse-derived
          {builtAt ? ` (last build ${builtAt})` : ""} — it reflects the last warehouse build, not
          rules published since. Active rules and Active overrides are current.
        </Typography>
      </Box>

      {/* ── Add a level ── */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start", mb: 1.5 }}>
        <TextField size="small" label="Level name (required)" value={name}
          onChange={(e) => setName(e.target.value)} sx={{ minWidth: 240 }} disabled={saving} />
        <FormControl size="small" sx={{ minWidth: 120 }} disabled={saving}>
          <InputLabel id="clv-program">Program</InputLabel>
          <Select labelId="clv-program" label="Program" value={programType}
            onChange={(e) => setProgramType(e.target.value)}>
            <MenuItem value="LK">LK</MenuItem>
            <MenuItem value="SI">SI</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 260 }} disabled={saving}>
          <InputLabel id="clv-after">Place after</InputLabel>
          <Select labelId="clv-after" label="Place after" value={placeAfter}
            onChange={(e) => setPlaceAfter(e.target.value)}>
            <MenuItem value={PLACE_FIRST}>(first in {programType})</MenuItem>
            {anchorItems}
          </Select>
        </FormControl>
        <TextField size="small" label="Age band" value={ageGroup}
          onChange={(e) => setAgeGroup(e.target.value)} sx={{ width: 130 }} disabled={saving} />
        <TextField size="small" label="Level no." value={levelNumber}
          onChange={(e) => setLevelNumber(e.target.value)} sx={{ width: 110 }} disabled={saving} />
        <Button variant="contained" size="small" disabled={!canSave} onClick={handleAdd}>
          {saving ? <CircularProgress size={18} /> : "Add level"}
        </Button>
      </Box>
      <FormControlLabel sx={{ mb: 1 }}
        control={<Checkbox size="small" checked={ruleTargetable} disabled={saving}
          onChange={(e) => setRuleTargetable(e.target.checked)} />}
        label="Available as a reusable-rule target" />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Uncheck for a level that records a per-class decision rather than a naming pattern, such as
        Other. It stays selectable for manual assignment in Class Audit and is refused as a rule
        target by the server, not merely hidden.
      </Typography>

      {addError && <Alert severity="error" sx={{ mb: 1 }}>{addError}</Alert>}
      {addNotice && <Alert severity="success" sx={{ mb: 1 }}>{addNotice}</Alert>}
      {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
      {loading && <CircularProgress size={20} />}

      {!loading && !loadError && levels.length === 0 && (
        <Typography variant="body2" color="text.secondary">No canonical levels found.</Typography>
      )}

      {levels.length > 0 && (
        <div className="table-responsive">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="text-start">Level</TableCell>
                <TableCell className="text-start">Program</TableCell>
                <TableCell className="text-start">Age band</TableCell>
                <TableCell className="text-start">Level no.</TableCell>
                <TableCell className="text-start">Sort</TableCell>
                <TableCell className="text-start">Status</TableCell>
                <TableCell className="text-start">Usable by</TableCell>
                <TableCell className="text-start">Classes mapped</TableCell>
                <TableCell className="text-start">Active rules</TableCell>
                <TableCell className="text-start">Active overrides</TableCell>
                <TableCell className="text-start">Added by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{rowEls}</TableBody>
          </Table>
        </div>
      )}
    </Paper>
  );
}
